import {useTranslation} from "react-i18next";
import React, {useState} from "react";
import {useForm} from "react-hook-form";
import {FileDto} from "../../../Apis/Models/FileDto";
import FlexRow from "../../../Components/FlexRow";
import styles from "./AnnualReportsPage.module.scss";
import CloseIcon from "../../../Assets/Icons/CloseIcon";
import Modal from "../../../Components/Modal";
import FileUploadButton from "../../../Components/FileUploadButton";
import {
    useAnnualReports,
    useDeleteAnnualReport,
    useSaveAnnualReport
} from "../../../Apis/Admin/SolarParkAnnualReportApi";
import {ISolarParkAnnualReportDto} from "../../../Apis/Models/ISolarParkAnnualReportDto";
import { saveAs } from 'file-saver';
import List from "../../../Components/List";
import {useSolarParks} from "../../../Apis/Admin/SolarParkAdminApi";
import Box from "../../../Components/Box";
import {StyledText} from "../../../Components/Text";
import Button from "Components/Button";
import Space from "../../../Components/Space";
import {ControlledInput} from "../../../Components/Input";
import InputLabel from "../../../Components/InputLabel";

const AnnualReportsPage = () => {

    const { t } = useTranslation();

    const { data: annualReports, isLoading: isLoadingReports } = useAnnualReports();
    const { data: solarParks } = useSolarParks();
    const { mutateAsync: saveAnnualReport, isLoading: isSavingReport } = useSaveAnnualReport();
    const { mutateAsync: deleteAnnualReportMutation, isLoading: isDeletingReport } = useDeleteAnnualReport();

    const [createReportModalVisible, setCreateReportModalVisible] = useState<boolean>(false);
    const [deleteReportModalVisible, setDeleteReportModalVisible] = useState<boolean>(false);

    const { handleSubmit, register, control, reset } = useForm<ISolarParkAnnualReportDto>();
    const [file, setFile] = useState<FileDto | null>(null);
    const [reportToDelete, setReportToDelete] = useState<ISolarParkAnnualReportDto>();

    const saveReport = async (data: ISolarParkAnnualReportDto) => {
        if (!file) {
            return;
        }

        await saveAnnualReport({ ...data, file: file });

        closeModal();
    }

    const closeModal = () => {
        setCreateReportModalVisible(false);
        setDeleteReportModalVisible(false);
        reset({
            id: '',
            name: '',
        });
        setFile(null)
        setReportToDelete(undefined);
    }

    const removeFile = () => {
        setFile(null);
    }

    const showDeleteReportModal = (report: ISolarParkAnnualReportDto) => {
        setReportToDelete(report);
        setDeleteReportModalVisible(true);
    }

    const deleteReport = async () => {
        await deleteAnnualReportMutation(reportToDelete!);

        closeModal();
    }

    const downloadReport = async (report: ISolarParkAnnualReportDto) => {
        const response = await fetch(report.file.url);

        const blob = await response.blob();

        saveAs(blob, report.file.fileName);
    }

    return (
        <Box>
            <FlexRow justify={"space-between"} align={"center"} style={{paddingBottom: 30}}>
                <StyledText type={"Lead"} weight={"Bold"} color={"secondary"}>{t('annualReport:title')}</StyledText>

                <Button color={"link"} onClick={() => setCreateReportModalVisible(true)} text={`+ ${t('annualReport:createAnnualReport')}`} />
            </FlexRow>

            <List data={annualReports} loading={isLoadingReports} columns={[
                {
                    dataIndex: 'name',
                    title: t('annualReport:name'),
                },
                {
                    dataIndex: 'solarPark',
                    title: t('solarPark'),
                    textRender: (value, record) => record.solarPark?.name
                },
                {
                    title: t('solarPark:functions'),
                    customRender: (_, report: ISolarParkAnnualReportDto) => (
                        <Space fit direction={"row"} size={"large"}>
                            <Button color={"link"} onClick={() => downloadReport(report)} text={`${t('annualReport:downloadReport')}`} />
                            <Button color={"link"} onClick={() => showDeleteReportModal(report)} text={`${t('annualReport:deleteAnnualReport')}`} />
                        </Space>
                    )
                },
            ]} emptyText={t('annualReport:noReportsFound')} keyDataIndex={"id"}
            />

            <Modal closeOnOutsideClick title={t('annualReport:createAnnualReport')} visible={createReportModalVisible} onCancel={closeModal}>
                <form onSubmit={handleSubmit(saveReport)}>
                    <input type={"hidden"} {...register('id')} />

                    <Space direction={"column"} size={"large"} >
                        <div className={"w-100"}>
                            <InputLabel>
                                {t('annualReport:name')}
                            </InputLabel>
                            <ControlledInput name={"name"} control={control} placeholder={t('name')} />
                        </div>

                        <div className={"w-100"}>
                            <InputLabel>
                                {t('solarPark')}
                            </InputLabel>
                            <select className={"select"} {...register('solarParkId')}>
                                {solarParks?.map(x => (
                                    <option value={x.id}>{x.name}</option>
                                ))}
                            </select>
                        </div>

                        <FlexRow>
                            {!file && (<FlexRow style={{padding: 40}} justify={"center"}><FileUploadButton callback={setFile} /></FlexRow>)}
                            {!!file && (
                                <FlexRow justify={"space-between"}>
                                    <div onClick={removeFile} className={styles.remove}>
                                        <CloseIcon fill={"red"} />
                                    </div>
                                </FlexRow>
                            )}
                        </FlexRow>

                        <FlexRow justify={"end"}>
                            <Button disabled={!file} state={isSavingReport ? 'Loading' : 'None'} type={"submit"} text={t('save')} />
                        </FlexRow>
                    </Space>
                </form>
            </Modal>

            <Modal closeOnOutsideClick title={t('annualReport:deleteAnnualReportHeader')} visible={deleteReportModalVisible} onCancel={closeModal}>
                <Space direction={"column"} size={"large"}>
                    <div className={"w-100"}>
                        {t('annualReport:deleteAnnualReportText')}
                    </div>

                    <FlexRow justify={"center"}>
                        <p>
                            {reportToDelete?.name}
                        </p>
                    </FlexRow>

                    <FlexRow justify={"end"}>
                        <Button disabled={isDeletingReport} state={isDeletingReport ? 'Loading' : 'None'} onClick={deleteReport} text={t('delete')} />
                    </FlexRow>
                </Space>
            </Modal>
        </Box>
    )

}

export default AnnualReportsPage
